import { useContext, useEffect, useMemo, useState } from 'react';
import NavBarComponent from './NavBar/NavBar';
import { Stack, useMediaQuery, useTheme } from '@mui/material';
import { DataContext } from '@context/DataContext';
import {
	AdditionalAnswerOptions,
	Answer,
	ANSWER_SOURCE,
	InvestigationDetails,
	Question,
	questionType,
} from '@/types/QuestionTypes';
import { INVESTIGATION_ANSWERED_BY, INVESTIGATION_STATUS } from '@/types/GeneralTypes';
import WelcomeScreenComponent from './WelcomeScreen/WelcomeScreen';
import ProgressBarComponent from './ProgressBar';
import QuestionComponent, { SessionMetrics } from './Questions/Question';
import { InvestigationContext } from '@context/InvestigationContext';
import FooterComponent from './Footer/Footer';
import { progressBarConstants } from '@/Config/constantValues';
import { LocalizationContext } from '@context/LocalizationContext';
import FinalQuestionComponent from './Questions/FinalQuestion';
import { useSetUserMetrics } from '@hooks/useSetUserMetrics';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from 'src/types/RefineAnalyticsEvents';
import { AddSupportingDocumentsComponent } from './FileUpload/AddSupportingDocuments';
import CompletedComponent from './Completed';
import { useLoading } from '@context/LoadingContext';
import ErrorScreenComponent from './ErrorHandling/ErrorScreen';
import InvalidUrlScreenComponent from './InvalidUrlScreen';
import LoadingSubmissionComponent from './Questions/LoadingSubmission';
import {
	BeginInvestigationActionButtons,
	EmailSentSuccessfullyActionButtons,
	RegularInvestigationActionButtons,
	UploadAttachmentsActionButtons,
} from './InvestigationUIActionButtons';
import { DowntimeScreen } from './DowntimeScreen';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { ExpiredLinkScreen } from './ExpiredLinkScreen/ExpiredLinkScreen';
import { useQueryInvestigationBegan } from 'src/hooks/useQueryInvestigationBegan';
import { RotateScreen } from './RotateScreen/RotateScreen';
import { CutoffScreen } from './CutoffScreen/CutoffScreen';
import { useQueryAnswerQuestion } from '@hooks/useQueryAnswerQuestion';
import { useQueryCompleteInvestigation } from '@hooks/useQueryCompleteInvestigation';

const getOrientation = () => {
	return window.matchMedia('(orientation: portrait)').matches ? 'portrait' : 'landscape';
};
export type NextButtonValidation = {
	isNextButtonDisabled: boolean;
	validateAfterClick: boolean;
};

export const InvestigationUI = () => {
	const { sc } = useContext(DataContext);
	const {
		attachments,
		investigationDetails,
		addAnswerToInvestigationDetails,
		toPreviousScreen,
		investigationStatus: status,
		updateInvestigationStatus,
		isError,
		isLoading,
		raiseError,
		supportLandscape,
	} = useContext(InvestigationContext);
	const { LanguageCode, Navigation, OUCF, WelcomeScreen, FileUpload } = useContext(LocalizationContext);
	// const { fetchCompleteInvestigation } = httpProvider;
	const [questionId, setQuestionId] = useState<string>('');
	const [progress, setProgress] = useState<number>(0); // 0 to 100
	const { setUserMetrics } = useSetUserMetrics();
	const { trackRefineEvent } = useAnalytics();
	const { loading, setLoading } = useLoading();
	const [isFileUploaded, setIsFileUploaded] = useState(false);
	const [currentAnswer, setCurrentAnswer] = useState<any>();
	const theme = useTheme();
	const greaterThanSmall = useMediaQuery(theme.breakpoints.up('sm'));
	const [emailSentScreen, setEmailSentScreen] = useState(false);
	const [investigationUIComponentKey, setInvestigationUIComponentKey] = useState(0);
	const [checkboxInput, setCheckboxInput] = useState(false);
	const [checkboxError, setCheckboxError] = useState(false);
	const [oucfSubmitting, setOucfSubmitting] = useState(false);
	const [orientation, setOrientation] = useState(getOrientation());
	const { investigationBegan } = useQueryInvestigationBegan();
	const oucfInvestigation = investigationDetails?.investigationType === 'oucf';
	const eddInvestigation = investigationDetails?.investigationType === 'edd';
	const ctrInvestigation = investigationDetails?.nextQuestion?.type == 'ctrQuestion';

	useEffect(() => {
		if (attachments?.length) {
			setIsFileUploaded(true);
		}
	}, [attachments]);

	// Update investigation status and progress bar
	useEffect(() => {
		if (investigationDetails?.nextQuestion) {
			const isAttachmentsUploaded = Boolean(
				investigationDetails?.nextQuestion?.additionalOptions?.attachments?.length,
			);
			setIsFileUploaded(isAttachmentsUploaded);
			setQuestionId(investigationDetails.nextQuestion.qid);
			setProgress(investigationDetails.nextQuestion.priorProgress);
		}
		if (investigationDetails?.status === INVESTIGATION_STATUS.INPROGRESS && !investigationDetails?.nextQuestion) {
			updateInvestigationStatus(INVESTIGATION_STATUS.SUBMITTING);
			return;
		}
		if (
			investigationDetails?.status === INVESTIGATION_STATUS.INPROGRESS ||
			investigationDetails?.status === INVESTIGATION_STATUS.FINALQUESTION
		) {
			updateInvestigationStatus(investigationDetails?.status);
		} else if (investigationDetails?.status === INVESTIGATION_STATUS.UPLOADFILE) {
			updateInvestigationStatus(INVESTIGATION_STATUS.UPLOADFILE);
			if (!investigationDetails?.nextQuestion) {
				setProgress(progressBarConstants.uploadFiles);
			}
		} else if (investigationDetails?.status === INVESTIGATION_STATUS.COMPLETED) {
			updateInvestigationStatus(INVESTIGATION_STATUS.COMPLETED);
			setProgress(100);
			trackRefineEvent(REFINE_ANALYTICS_EVENTS.INVESTIGATION_COMPLETED_SCREEN_LOADED);
		}
	}, [investigationDetails]);

	// Reset answer state when moving to a new question (for OUCF multi-check scenarios)
	// This ensures each check/question starts with a clean slate (no pre-selected answers)
	// Exception: Don't reset when transitioning to FINALQUESTION - FinalQuestion component
	// will set its own state including additionalOptions for file upload checkbox
	useEffect(() => {
		if (investigationDetails?.nextQuestion?.qid && investigationDetails?.status !== INVESTIGATION_STATUS.FINALQUESTION) {
			setCurrentAnswer({ answerId: '', answerText: '' });
			setCheckboxInput(false);
			setCheckboxError(false);
		}
	}, [investigationDetails?.nextQuestion?.qid, investigationDetails?.status]);

	const handleCheckboxChange = (checked: boolean) => {
		setCheckboxInput(checked);
		setCheckboxError(!checked);
	};
	const { postCompleteInvestigation } = useQueryCompleteInvestigation();

	// Submits (via POST request) complete investigation to server
	const completeInvestigation = async (
		answeredBy: INVESTIGATION_ANSWERED_BY,
		moodDescription?: string,
		mood?: string,
	) => {
		const currentInvestigationStatus = investigationDetails?.status;
		const currentInvestigationProgress = investigationDetails?.nextQuestion?.priorProgress;
		updateInvestigationStatus(INVESTIGATION_STATUS.SUBMITTING);
		setLoading(true);

		// This is any because the body of the object is dynamic, and then stringified
		let requestBody: any = {
			answeredBy: INVESTIGATION_ANSWERED_BY.CLIENT,
			emotionalState: mood ?? null,
			notes: moodDescription ?? '',
		};

		if (sc) {
			requestBody.sc = sc;
		}

		const res = await postCompleteInvestigation({ requestBody });
		if (!res) {
			setLoading(false);
			updateInvestigationStatus(currentInvestigationStatus);
			setProgress(currentInvestigationProgress || 0);
			raiseError("Couldn't complete investigation");
			return res;
		}
		setProgress(progressBarConstants.questionnaireEnd);
		updateInvestigationStatus(INVESTIGATION_STATUS.COMPLETED);
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.INVESTIGATION_COMPLETED);
		return res;
	};

	const handleOnClickBeginning = () => {
		// Track investigation begin event
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.BEGIN_BUTTON_CLICKED);
		investigationBegan();

		if (investigationDetails && investigationDetails.status === INVESTIGATION_STATUS.BEGINNING) {
			const timeInContext = new Date().getTime() - Number(sessionStorage.getItem('investigationContextOpened'));

			setUserMetrics({
				actionType: 'beginInvestigation',
				componentType: 'investigationContext',
				questionId: 'investigationContext',
				timeSpent: timeInContext,
				sc: sc,
			});
			sessionStorage.removeItem('investigationContextOpened');
		}

		if (investigationDetails) {
			setQuestionId(investigationDetails?.nextQuestion?.qid);
			setProgress(investigationDetails?.nextQuestion?.priorProgress || progressBarConstants.firstQuestionStart);
		}
		if (
			investigationDetails?.status === INVESTIGATION_STATUS.BEGINNING ||
			investigationDetails?.status === INVESTIGATION_STATUS.INPROGRESS
		) {
			updateInvestigationStatus(INVESTIGATION_STATUS.INPROGRESS);
		} else if (investigationDetails?.status === INVESTIGATION_STATUS.FINALQUESTION) {
			updateInvestigationStatus(INVESTIGATION_STATUS.FINALQUESTION);
		} else if (investigationDetails?.status === INVESTIGATION_STATUS.UPLOADFILE) {
			updateInvestigationStatus(INVESTIGATION_STATUS.UPLOADFILE);
		} else if (investigationDetails?.status) {
			updateInvestigationStatus(investigationDetails.status);
		}
	};

	const { postAnswerQuestion } = useQueryAnswerQuestion();

	async function postAnswer(questionId: string, answer: Answer) {
		setLoading(true);

		const answerBody: any = {
			questionId: questionId,
			answeredBy: answer.answeredBy.toString() as ANSWER_SOURCE,
			answerId: answer.answerId,
			answerText: answer.answerText,
			lang: answer.lang,
			openAnswer: answer.openAnswer ?? undefined,
			additionalOptions: answer?.additionalOptions ?? undefined,
		};

		if (sc) {
			answerBody.sc = sc;
		}
		let timeToAnswer = 0;

		const storedMetrics = sessionStorage.getItem('sessionMetrics');
		if (storedMetrics) {
			const sessionMetrics = JSON.parse(storedMetrics) as SessionMetrics[];
			const currentQuestionMetrics = sessionMetrics.find((metric) => metric.questionId === questionId);
			if (currentQuestionMetrics) {
				timeToAnswer = new Date().getTime() - currentQuestionMetrics.openedTime;
			}
		}

		setUserMetrics({
			actionType: 'answerQuestion',
			componentType: 'question',
			questionId: questionId,
			timeSpent: timeToAnswer,
			sc: sc,
		});

		const res = await postAnswerQuestion({ answerBody });
		if (!res) {
			setLoading(false);
			raiseError("Couldn't fetch question");
			return res;
		}
		setProgress(res.nextQuestion?.priorProgress ?? 100);
		setLoading(false);
		return res;
	}

	const [isTextInputInvalid, setIsTextInputInvalid] = useState(false);
	const [isNaicsSelectInvalid, setisNaicsSelectInvalid] = useState(false);

	const createOUCFAnswer = ({ currentAnswer, lang }: any) => ({
		answeredBy: ANSWER_SOURCE.CLIENT,
		answerId: currentAnswer.answerId,
		answerText: currentAnswer.answerText,
		lang: lang || 'en-US',
		openAnswer: currentAnswer.answerText,
		additionalOptions: undefined,
	});

	const createNaicsAnswer = ({ currentAnswer, lang }: any) => ({
		answeredBy: ANSWER_SOURCE.CLIENT,
		answerId: currentAnswer.answerId,
		answerText: currentAnswer.answerText,
		lang: lang || 'en-US',
		openAnswer: currentAnswer.answerText,
		additionalOptions: undefined,
	});

	const completeClientInvestigation = async ({ answer }: any) => {
		const completeInvestigationWithDelay = async () => {
			await completeInvestigation(INVESTIGATION_ANSWERED_BY.CLIENT);
			setOucfSubmitting(false);
		};

		setOucfSubmitting(true);
		const postAnswerRes = await postAnswer(investigationDetails!.nextQuestion.qid, answer);

		if (!postAnswerRes) {
			setOucfSubmitting(false);
			raiseError(`Couldn't post answer: ${JSON.stringify(answer)}`);
			return;
		}
		setTimeout(() => {
			completeInvestigationWithDelay();
		}, 3500);
		return;
	};

	const handleNextClick = async (isNextButtonDisabled = false) => {
		if (isNextButtonDisabled) {
			setIsTextInputInvalid(true);
			setCheckboxError(true);
			setisNaicsSelectInvalid(true);
			return;
		}

		// REMOVED: Immediate completion for check YES answers (lines 302-310)
		// This logic prevented multiple checks from being processed
		// Now let the normal flow continue and check backend response

		// REMOVED: Immediate completion for OUCF multiple choice (lines 312-320)
		// Question 2 (explanation) should not auto-complete - backend decides next step

		if (
			status === INVESTIGATION_STATUS.INPROGRESS &&
			eddInvestigation &&
			investigationDetails?.nextQuestion?.type === 'dynamicOptions'
		) {
			if (!currentAnswer?.answerId) {
				await completeInvestigation(INVESTIGATION_ANSWERED_BY.CLIENT);
				return;
			}

			const answer = createNaicsAnswer({ currentAnswer, lang: LanguageCode });
			await completeClientInvestigation({ answer });
			return;
		}

		if (
			(status === INVESTIGATION_STATUS.INPROGRESS &&
				eddInvestigation &&
				investigationDetails?.nextQuestion?.type === 'openAnswer' &&
				investigationDetails?.nextQuestion?.qid === '2') ||
			(investigationDetails?.nextQuestion?.type === 'multipleChoice' &&
				investigationDetails?.nextQuestion?.qid === '5')
		) {
			const answer = createNaicsAnswer({ currentAnswer, lang: LanguageCode });
			await completeClientInvestigation({ answer });
			return;
		}

		if (status === INVESTIGATION_STATUS.FINALQUESTION) {
			trackRefineEvent(REFINE_ANALYTICS_EVENTS.OPTIONAL_QUESTION_NEXT_BUTTON_CLICKED);
			await handleOnClickFinalQuestion(
				currentAnswer.answerId,
				currentAnswer.answerText,
				currentAnswer.additionalOptions,
			);
			return;
		}
		await handleOnClickQuestion(currentAnswer.answerId, currentAnswer.answerText);
		setCurrentAnswer({ answerId: '', answerText: '' });
	};

	const handleOnClickQuestion = async (
		selectedAnswerId: string,
		openAnswer: string,
		additionalOptions?: AdditionalAnswerOptions,
	) => {
		const answer = {
			answeredBy: ANSWER_SOURCE.CLIENT,
			answerId: selectedAnswerId,
			answerText: openAnswer,
			lang: LanguageCode || 'en-US',
			openAnswer,
			additionalOptions: additionalOptions ?? undefined,
		};

		const questionResponse = await postAnswer(investigationDetails!.nextQuestion.qid, answer);
		if (!questionResponse) return;

		const nextQuestion: Question = questionResponse.nextQuestion;
		addAnswerToInvestigationDetails(questionResponse, answer);

		if (questionResponse.status === INVESTIGATION_STATUS.INPROGRESS && nextQuestion) {
			setQuestionId(nextQuestion.qid);
		} else if (questionResponse.status === INVESTIGATION_STATUS.FINALQUESTION) {
			setQuestionId(nextQuestion.qid);
			updateInvestigationStatus(INVESTIGATION_STATUS.FINALQUESTION);
		} else if (questionResponse.status === INVESTIGATION_STATUS.UPLOADFILE) {
			updateInvestigationStatus(INVESTIGATION_STATUS.UPLOADFILE);
			if (nextQuestion) {
				setQuestionId(nextQuestion.qid);
			}
		} else if (questionResponse.nextQuestion === null) {
			// Backend signals completion - no more questions/checks to process
			// This is the primary completion path for multiple-check OUCF investigations
			await completeInvestigation(INVESTIGATION_ANSWERED_BY.CLIENT);
		}
	};

	const handleOnClickFinalQuestion = async (
		selectedAnswerId: string,
		openAnswer: string,
		additionalOptions?: AdditionalAnswerOptions,
	) => {
		const answer = {
			answeredBy: ANSWER_SOURCE.CLIENT,
			answerId: selectedAnswerId,
			answerText: openAnswer === '' ? '' : openAnswer,
			lang: LanguageCode || 'en-US',
			openAnswer,
			additionalOptions: additionalOptions ?? undefined,
		};

		const questionResponse = await postAnswer(investigationDetails!.nextQuestion.qid, answer);
		if (!questionResponse) return;

		const nextQuestion: Question = questionResponse.nextQuestion;
		addAnswerToInvestigationDetails(questionResponse, answer);

		if (additionalOptions?.fileUploadChecked) {
			updateInvestigationStatus(INVESTIGATION_STATUS.UPLOADFILE);
			if (nextQuestion?.qid) {
				setQuestionId(nextQuestion.qid);
			}
		} else {
			// Complete investigation
			await completeInvestigation(INVESTIGATION_ANSWERED_BY.CLIENT);
		}
	};

	const handleOnClickUploadAttachments = async () => {
		await completeInvestigation(INVESTIGATION_ANSWERED_BY.CLIENT);
	};

	const handleOnClickPrev = () => {
		setIsTextInputInvalid(false);
		setCheckboxError(false);
		setisNaicsSelectInvalid(false);
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.PREVIOUS_BUTTON_CLICKED, {
			question: investigationDetails?.nextQuestion,
		});
		if (investigationDetails) {
			setProgress(toPreviousScreen());
		}
	};

	const handleAnswerChange = (id: any, answerText: any, additionalOptions?: any) => {
		//Reset checkbox value when answer changes
		setCheckboxInput(false);
		setCheckboxError(false);
		setisNaicsSelectInvalid(false);
		if (answerText?.length || answerText == null) {
			setIsTextInputInvalid(false);
		} else {
			setIsTextInputInvalid(true);
		}
		setCurrentAnswer({ answerId: id, answerText: answerText, additionalOptions: additionalOptions });
	};

	const [submitWithoutDocumentsDialogOpen, setSubmitWithoutDocumentsDialogOpen] = useState(false);

	const validateNextButton = (): NextButtonValidation => {
		if (ctrInvestigation) {
			return {
				isNextButtonDisabled: !currentAnswer?.answerId,
				validateAfterClick: true,
			};
		}

		if (
			oucfInvestigation &&
			currentAnswer?.answerId === '1' &&
			investigationDetails?.nextQuestion?.type !== 'multipleChoice'
		) {
			return {
				isNextButtonDisabled: !currentAnswer?.answerId || !checkboxInput,
				validateAfterClick: true,
			};
		}

		if (eddInvestigation && investigationDetails?.nextQuestion?.type === 'dynamicOptions') {
			return {
				isNextButtonDisabled: false,
				validateAfterClick: false,
			};
		}

		if (status === INVESTIGATION_STATUS.INPROGRESS) {
			const isOpenAnswer =
				isAnswerOpen(currentAnswer?.answerId) || investigationDetails?.nextQuestion?.type === 'openAnswer';
			const isAnswerEmpty = !currentAnswer?.answerText;

			return {
				isNextButtonDisabled: isOpenAnswer ? isAnswerEmpty : !currentAnswer?.answerId,
				validateAfterClick: isOpenAnswer,
			};
		}

		if (status === INVESTIGATION_STATUS.FINALQUESTION) {
			return {
				isNextButtonDisabled: false,
				validateAfterClick: false,
			};
		}

		return {
			isNextButtonDisabled: false,
			validateAfterClick: false,
		};
	};

	const showErrorScreen = isError || !sc;

	const showRegularActionButtons =
		(status === INVESTIGATION_STATUS.INPROGRESS || status === INVESTIGATION_STATUS.FINALQUESTION) &&
		!isLoading &&
		!showErrorScreen;

	const showUploadFileActionButtons =
		status === INVESTIGATION_STATUS.UPLOADFILE && !isLoading && !emailSentScreen && !showErrorScreen;

	const showProgressBar =
		status !== INVESTIGATION_STATUS.SUBMITTING &&
		status !== INVESTIGATION_STATUS.COMPLETED &&
		!isLoading &&
		!showErrorScreen;

	const showBeginActionButtons = status === INVESTIGATION_STATUS.BEGINNING && !isLoading && !showErrorScreen;
	let showFooter;

	if (greaterThanSmall) {
		showFooter = true;
	}
	if (!greaterThanSmall && status === INVESTIGATION_STATUS.BEGINNING) {
		showFooter = true;
	}
	const emailSentPrevFunction = () => {
		setEmailSentScreen(false);
		sessionStorage.setItem('emailData', JSON.stringify({ email: '', emailSent: false }));
		setInvestigationUIComponentKey((prevKey) => prevKey + 1);
	};

	const isAnswerOpen = (answerId: string | null): boolean => {
		if (answerId && investigationDetails && investigationDetails.nextQuestion) {
			if (investigationDetails.nextQuestion.possibleAnswers[answerId]) {
				return investigationDetails.nextQuestion.possibleAnswers[answerId].isOpen;
			}
		}

		return false;
	};

	useEffect(() => {
		const mediaQuery = window.matchMedia('(orientation: portrait)');
		const handleChange = () => setOrientation(getOrientation());
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.OUCF_CUSTOMER_ROTATES_SCREEN);

		if (mediaQuery.addEventListener) {
			mediaQuery.addEventListener('change', handleChange);
		} else {
			mediaQuery.addListener(handleChange); // Fallback for older iOS versions
		}

		return () => {
			if (mediaQuery.removeEventListener) {
				mediaQuery.removeEventListener('change', handleChange);
			} else {
				mediaQuery.removeListener(handleChange); // Fallback for older iOS versions
			}
		};
	}, [orientation]);

	const showDowntimeScreen = useFeatureFlagEnabled('showDowntimeScreen');
	const showLanguageSelectorFlag = useFeatureFlagEnabled('showLanguageSelector');
	const showExpiredLinkScreen = investigationDetails?.status === 'expired';

	let showLanguageSelector = true;

	if (eddInvestigation) {
		showLanguageSelector = Boolean(showLanguageSelectorFlag);
	}

	if (investigationDetails?.status === INVESTIGATION_STATUS.CUTOFF) {
		return (
			<CutoffScreen
				checkImageURL={investigationDetails?.checkImage?.signedUrl ?? ''}
				checkNumber={investigationDetails?.checkNumber ?? ''}
				checkAmount={investigationDetails?.checkNumber ?? ''}
				mainText={investigationDetails?.context ?? ''}
				noteText={investigationDetails?.note ?? ''}
				partyName={investigationDetails?.partyName ?? ''}
			/>
		);
	}

	if (showExpiredLinkScreen) {
		return <ExpiredLinkScreen />;
	}

	if (showDowntimeScreen) {
		return <DowntimeScreen />;
	}

	if (oucfSubmitting) {
		return (
			<Stack width={'100%'} height={`100svh`}>
				<NavBarComponent showLanguageSelector={showLanguageSelector} isQuestionTrailDisplayed={false} />
				<LoadingSubmissionComponent />
			</Stack>
		);
	}
	const isMobile = /Mobi|Android/i.test(navigator.userAgent);

	if (isMobile && orientation === 'landscape' && !supportLandscape) {
		return (
			<Stack width={'100%'} height={`100svh`}>
				<NavBarComponent showLanguageSelector={true} isQuestionTrailDisplayed={false} />
				<RotateScreen />
			</Stack>
		);
	}

	const currentQuestionType = investigationDetails?.nextQuestion?.type;

	let nextButtonText = Navigation.Next;

	// UPDATED: For OUCF investigations, always show "Next" (not "Submit")
	// We cannot know if investigation will complete until backend responds
	// Backend returns null to signal completion, which triggers completeInvestigation()
	// This applies to both check questions and explanation questions (Question 2)

	if (eddInvestigation && currentQuestionType === 'dynamicOptions') {
		nextButtonText = OUCF.SubmitButton;
	}

	if (showErrorScreen) {
		return (
			<Stack height={'100%'} width={'100%'}>
				<NavBarComponent isQuestionTrailDisplayed={false} showLanguageSelector={true} />
				<Stack>
					{/* Invalid params screen */}
					{!sc && <InvalidUrlScreenComponent />}

					{/* Error screen */}
					{isError && sc && <ErrorScreenComponent error={isError} />}
				</Stack>
			</Stack>
		);
	}
	return (
		<Stack key={investigationUIComponentKey} width={'100%'} height={`100svh`}>
			<NavBarComponent showLanguageSelector={showLanguageSelector} isQuestionTrailDisplayed={false} />
			<Stack
				paddingBottom={'8px'}
				paddingInline={{
					xxs: '24px',
					xs: '24px',
					sm: '48px',
					md: '105px',
					lg: '350px',
					xl: '403px',
				}}
				paddingTop={{
					xxs: '16px',
					xs: '16px',
					sm: '32px',
					md: '32px',
					lg: '32px',
					xl: '32px',
				}}
				height={'100%'}
				overflow={'scroll'}>
				<Stack gap={'24px'} justifyContent={'space-between'} height={'100%'} overflow={'scroll'}>
					{status === INVESTIGATION_STATUS.BEGINNING && (
						<WelcomeScreenComponent handleOnClickNext={handleOnClickBeginning} />
					)}

					{status === INVESTIGATION_STATUS.INPROGRESS && (
						<QuestionComponent
							required={eddInvestigation}
							isSelectInvalid={isNaicsSelectInvalid}
							isTextInputInvalid={isTextInputInvalid}
							isCheckboxInvalid={checkboxError}
							handleCheckboxChange={handleCheckboxChange}
							setCurrentAnswer={handleAnswerChange}
							questionId={questionId}
						/>
					)}

					{status === INVESTIGATION_STATUS.FINALQUESTION && (
						<FinalQuestionComponent setCurrentAnswer={handleAnswerChange} questionId={questionId} />
					)}
					{status === INVESTIGATION_STATUS.UPLOADFILE && (
						<AddSupportingDocumentsComponent
							setIsEmailSent={setEmailSentScreen}
							isSubmitWithoutDocumentsDialogOpen={submitWithoutDocumentsDialogOpen}
							setIsSubmitWithoutDocumentsDialogOpen={setSubmitWithoutDocumentsDialogOpen}
							handleSubmit={handleOnClickUploadAttachments}
							setIsFileUploaded={setIsFileUploaded}
							isFileUploaded={isFileUploaded}
						/>
					)}
					{status === INVESTIGATION_STATUS.SUBMITTING && <LoadingSubmissionComponent />}
					{status === INVESTIGATION_STATUS.COMPLETED && <CompletedComponent />}

					<Stack gap={'24px'}>
						{showBeginActionButtons && (
							<BeginInvestigationActionButtons
								beginButtonText={oucfInvestigation ? OUCF.BeginButton : WelcomeScreen.Begin}
								handleOnClickBeginning={handleOnClickBeginning}
							/>
						)}
						{showRegularActionButtons && (
							<RegularInvestigationActionButtons
								handleOnClickPrev={handleOnClickPrev}
								prevButtonText={Navigation.Previous}
								nextButtonText={nextButtonText}
								nextButtonValidator={validateNextButton}
								loading={loading}
								handleNextClick={handleNextClick}
							/>
						)}
						{showUploadFileActionButtons && (
							<UploadAttachmentsActionButtons
								submitButtonDisabled={!isFileUploaded}
								handleOnClickPrev={handleOnClickPrev}
								loading={loading}
								setReviewSupportingDocumentsDialogOpen={handleOnClickUploadAttachments}
								prevButtonText={Navigation.Previous}
								submitButtonText={FileUpload.SubmitFormButton}
							/>
						)}
						{emailSentScreen && (
							<EmailSentSuccessfullyActionButtons
								handleOnClickPrev={emailSentPrevFunction}
								prevButtonText={Navigation.Previous}
							/>
						)}

						{showProgressBar && <ProgressBarComponent progressPercentage={progress} />}
					</Stack>
				</Stack>
			</Stack>
			{showFooter && <FooterComponent />}
		</Stack>
	);
};
